// Đợt Offline-1 (06/2026) — Network Governor v1: LỚP MỎNG điều phối mạng.
//
// Chỉ sở hữu 3 việc: (1) nhận tín hiệu mạng/lifecycle, (2) cho phép/không cho
// phát request nền, (3) điều phối thứ tự phục hồi. edgeApi TIẾP TỤC sở hữu
// breaker/stale/Retry-After/lastGood (degraded là per-policy của edgeApi).
//
// State: online | offline | reconnecting. Cờ riêng: background, serverWideBusy.
// Operational Context: route referee/publisher/admin → PASSIVE (không đụng
// onlineManager, không gate, không scheduler) — connection-lock trọng tài và
// watchdog publisher toàn quyền. KHÔNG offline queue.
import { onlineManager, focusManager } from "@tanstack/react-query";

const PASSIVE_PREFIXES = ["/referee", "/publisher", "/admin"];

const S = {
  state: "online",
  background: false,
  serverWideBusy: false,
  route: "/",
  criticalSessionActive: false,
  hasNativeBridge: typeof window !== "undefined" && !!window.ReactNativeWebView,
  recoverAt: { primary: 0, ws: 0, heartbeat: 0, polling: 0 },
  stats: { offline_events: 0, reconnects: 0, dropped_visits: 0, dropped_beats: 0, blocked_mutations: 0 },
};
let _primaryTimer = null;
const _listeners = new Set();

function _emit() {
  for (const cb of _listeners) { try { cb(getNetworkState()); } catch { /* noop */ } }
}

export function subscribe(cb) { _listeners.add(cb); return () => _listeners.delete(cb); }

export function getNetworkState() {
  return { state: S.state, background: S.background, serverWideBusy: S.serverWideBusy, passive: isPassiveContext() };
}

export function isPassiveContext() {
  return S.criticalSessionActive || PASSIVE_PREFIXES.some((p) => S.route.startsWith(p));
}

export function isOffline() { return S.state === "offline"; }

export function setRoute(pathname) { S.route = pathname || "/"; }
export function setCriticalSessionActive(v) { S.criticalSessionActive = !!v; }
export function setServerWideBusy(v) { S.serverWideBusy = !!v; }

// Stable jitter theo visitor_id — thiết bị giữ nguyên slot khi mạng chập chờn.
function _stableJitter(minMs, maxMs) {
  let seed = 0;
  try {
    const vid = window.localStorage?.getItem("pl_visitor_id") || "";
    for (let i = 0; i < vid.length; i += 1) seed = (seed * 31 + vid.charCodeAt(i)) >>> 0;
  } catch { /* noop */ }
  const frac = ((seed % 1000) / 1000);
  return Math.round(minMs + frac * (maxMs - minMs));
}

function _goOffline(reason) {
  if (S.state === "offline") return;
  S.state = "offline";
  S.stats.offline_events += 1;
  if (_primaryTimer) { clearTimeout(_primaryTimer); _primaryTimer = null; }
  if (!isPassiveContext()) {
    try { onlineManager.setOnline(false); } catch { /* noop */ }
  }
  try { console.info("[governor] OFFLINE:", reason); } catch { /* noop */ }
  _emit();
}

function _goReconnecting() {
  if (S.state !== "offline") return;
  S.state = "reconnecting";
  S.stats.reconnects += 1;
  const now = Date.now();
  S.recoverAt.primary = now + _stableJitter(2_000, 12_000);
  S.recoverAt.ws = now + _stableJitter(5_000, 25_000);
  S.recoverAt.heartbeat = now + _stableJitter(10_000, 30_000);
  S.recoverAt.polling = now + _stableJitter(20_000, 40_000);
  // Primary stage: bật lại onlineManager ĐÚNG thời điểm jitter → RQ tự refetch
  // active queries của route hiện tại (refetchOnReconnect) = staged refetch,
  // không cần đổi RQ global defaults, không storm đồng loạt.
  if (!isPassiveContext()) {
    _primaryTimer = setTimeout(() => {
      _primaryTimer = null;
      try { onlineManager.setOnline(true); } catch { /* noop */ }
    }, S.recoverAt.primary - now);
  }
  _emit();
}

// Request thật thành công (edgeApi báo) → xác nhận online. Không health ping mới.
export function reportRequestOk() {
  if (S.state === "online") return;
  S.state = "online";
  S.serverWideBusy = false;
  if (!isPassiveContext()) {
    try { onlineManager.setOnline(true); } catch { /* noop */ }
  }
  _emit();
}

export function reportNetworkFail() {
  // 1 lỗi đơn lẻ KHÔNG chuyển app sang offline — chỉ khi connectivity signal
  // cũng báo mất. Ở đây chỉ dùng để giữ reconnecting (không lên online).
}

// kind: "visit" | "heartbeat" | "ws" | "polling"
export function canRun(kind) {
  if (isPassiveContext()) return true; // referee/publisher/admin: toàn quyền như cũ
  if (S.state === "offline") {
    if (kind === "visit") S.stats.dropped_visits += 1;
    if (kind === "heartbeat") S.stats.dropped_beats += 1;
    return false;
  }
  if (S.background && (kind === "heartbeat" || kind === "visit" || kind === "polling")) return false;
  if (S.state === "reconnecting") {
    if (kind === "visit") return false; // không gửi bù visit
    const at = S.recoverAt[kind === "polling" ? "polling" : kind];
    if (at && Date.now() < at) return false;
  }
  return true;
}

export function governorStats() { return { ...S.stats, state: S.state, route: S.route }; }

function _onConnectivity(online, source) {
  if (online) {
    if (S.state === "offline") _goReconnecting();
  } else {
    _goOffline(source);
  }
}

export function initNetworkGovernor() {
  if (typeof window === "undefined") return;
  // Nguồn 1 (ưu tiên trong app shell): native NetInfo qua bridge event.
  window.addEventListener("pl-native-network", (e) => {
    S.hasNativeBridge = true;
    _onConnectivity(!!e?.detail?.online, "native");
  });
  // Nguồn 2: browser events — bỏ qua tín hiệu OFFLINE của browser khi có
  // native bridge (navigator.onLine không đáng tin trong WKWebView iOS).
  window.addEventListener("offline", () => {
    if (!S.hasNativeBridge) _onConnectivity(false, "browser");
  });
  window.addEventListener("online", () => {
    if (!S.hasNativeBridge) _onConnectivity(true, "browser");
  });
  // Background: CHỈ focusManager — không phải network state.
  document.addEventListener("visibilitychange", () => {
    S.background = document.hidden;
    try { focusManager.setFocused(!document.hidden); } catch { /* noop */ }
    _emit();
  });
  try { window.__plGovernor = governorStats; } catch { /* noop */ }
}

// Chỉ dùng cho unit test.
export function _resetGovernorForTest() {
  S.state = "online"; S.background = false; S.serverWideBusy = false;
  S.route = "/"; S.criticalSessionActive = false;
  if (_primaryTimer) { clearTimeout(_primaryTimer); _primaryTimer = null; }
  Object.keys(S.stats).forEach((k) => { S.stats[k] = 0; });
}
export function _forceStateForTest(st) { S.state = st; }
